import PropTypes, { shape } from 'prop-types';
import Flux from '../../flux/Flux';
import { VXPay } from '../../utils/VXPay';
import VXButton from '../SimpleElements/VXButton/VXButton';
import { COLOR_TYPE_SPECIAL } from '../SimpleElements/VXButton/ButtonHelper';
import { VXButtonLineConfig } from '../SimpleElements/VXButton/VXButtonConfig';
import STYLES from '../SimpleElements/VXButton/EnumVXButton';
import { getCommonTranslation } from '../../utils/TranslationHelper';
import GuestAccess from '../../utils/GuestAccess';
import { formatCurrency } from '../../utils/CommonUtils';
import Translations from '../../utils/Translations';

const BUY_TYPES = {
	TG: 'tippingGoal',
	PC: 'paidContent',
};

function getButton({ isVideo = false, actorId }) {
	if (!Flux.Guest.isLoggedIn()) {
		const options = {
			host: actorId,
		};
		return (
			<div className={'feed-button' + (isVideo ? ' feed-button--video' : '')}>
				<VXButton
					color={COLOR_TYPE_SPECIAL}
					line1={[new VXButtonLineConfig(getCommonTranslation('login_or_signup_button').toUpperCase())]}
					onClick={() => VXPay.openSignupOrLogin(options)}
					size={STYLES.SIZE.SMALL}
				/>
			</div>
		);
	}
}

function getBuyButton({ postId, price, actorId }) {
	return (
		<div className={'feed-button -bottom-relative'}>
			<VXButton
				line1={[new VXButtonLineConfig(getCommonTranslation('buy') + ` (${formatCurrency(price)})`)]}
				color={COLOR_TYPE_SPECIAL}
				onClick={() => buyPost(postId, price, actorId)}
				icon={'-icon-cart-full'}
			/>
		</div>
	);
}

const REPLY_SIGN = '@';

function getTimeFromSeconds(sec) {
	const minutes = Math.floor(sec / 60);
	const seconds = sec - minutes * 60;

	return `${minutes < 1 ? '00' : (minutes < 10 ? '0' : 0) + minutes}:${(seconds < 10 ? '0' : '') + seconds} min`;
}

const postTypeTypes = [
	Flux.Constants.Feed.Types.TEXT,
	Flux.Constants.Feed.Types.HOT_CLIP,
	Flux.Constants.Feed.Types.CLIP,
	Flux.Constants.Feed.Types.PHOTOS,
	Flux.Constants.Feed.Types.CONTEST,
];
const postTypenameTypes = ['TextPost', 'ClipPost', 'PhotosPost'];

const userType = {
	id: PropTypes.string,
	name: PropTypes.string,
	avatar: PropTypes.shape({
		id: PropTypes.string,
		fsk: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		url: PropTypes.string,
		avsRequired: PropTypes.bool,
		srcSet: PropTypes.string,
	}),
};

const tippingGoalType = {
	goal: PropTypes.number,
	tipsSum: PropTypes.number,
	goalCoins: PropTypes.number,
	tipsSumCoins: PropTypes.number,
	progressPercent: PropTypes.number,
	achieved: PropTypes.bool,
};

const postType = {
	created: PropTypes.string,
	id: PropTypes.string,
	likes: PropTypes.number,
	modelId: PropTypes.string,
	model: PropTypes.shape(userType),
	status: PropTypes.string,
	text: PropTypes.string,
	title: PropTypes.string,
	type: PropTypes.oneOf(postTypeTypes),
	umaId: PropTypes.string,
	views: PropTypes.number,
	price: PropTypes.number,
	tippingGoal: PropTypes.shape(tippingGoalType),
	paidContent: PropTypes.bool,
	fsk: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	guestState: PropTypes.shape({
		viewed: PropTypes.bool,
		liked: PropTypes.bool,
		following: PropTypes.bool,
		bought: PropTypes.bool,
	}),
	__typename: PropTypes.oneOf(postTypenameTypes),
};

const postClipType = {
	height: PropTypes.number,
	width: PropTypes.number,
	length: PropTypes.number,
	ratio: PropTypes.number,
	avsRequired: PropTypes.bool,
	poster: PropTypes.shape({
		id: PropTypes.string,
		srcSet: PropTypes.string,
		url: PropTypes.string,
	}),
	sources: PropTypes.arrayOf(
		shape({
			label: PropTypes.string,
			src: PropTypes.string,
			type: PropTypes.string,
		}),
	),
};

const postPhotoType = {
	pinned: PropTypes.bool,
	contest: PropTypes.shape({
		id: PropTypes.string,
	}),
	photos: PropTypes.shape({
		id: PropTypes.string,
		srcSet: PropTypes.string,
		url: PropTypes.string,
		avsRequired: PropTypes.bool,
	}),
};

const postCommentType = {
	user: PropTypes.shape(userType),
	totalCount: PropTypes.number,
	nodes: PropTypes.arrayOf(
		PropTypes.shape({
			comment: PropTypes.shape({
				id: PropTypes.string,
				conversationId: PropTypes.string,
				parentId: PropTypes.string,
				created: PropTypes.string,
				isReplyToReply: PropTypes.bool,
				text: PropTypes.string,
				isCreator: PropTypes.bool,
				tipped: PropTypes.bool,
				user: PropTypes.shape(userType),
			}),
			replies: PropTypes.arrayOf(
				PropTypes.shape({
					id: PropTypes.string,
					created: PropTypes.string,
					text: PropTypes.string,
					isCreator: PropTypes.bool,
					tipped: PropTypes.bool,
					user: PropTypes.shape(userType),
				}),
			),
		}),
	),
};

const FEATURED_POSTS = [Flux.Constants.Feed.Types.FP_HOT_CLIP, Flux.Constants.Feed.Types.FP_PHOTO_GALLERY];

const FREE_CONTENT = [
	Flux.Constants.Feed.Types.FP_HOT_CLIP,
	Flux.Constants.Feed.Types.FP_PHOTO_GALLERY,
	Flux.Constants.Feed.Types.CLIP,
	Flux.Constants.Feed.Types.PHOTOS,
];

function isRecommended(type) {
	return FEATURED_POSTS.includes(type);
}

function setUrlParameter({ page = null, filter = null, onlyFavorites = false }) {
	const url = new URL(window.location.href);
	const params = url.searchParams;

	if (page) {
		params.set('page', page);
	}
	if (filter) {
		if (filter.length < 1) {
			params.delete('filter');
		} else {
			params.set('filter', filter.join(','));
		}
	}

	if (onlyFavorites) {
		params.set('favorites', true);
	} else {
		params.delete('favorites');
	}

	window.history.replaceState(window.history.state, null, '?' + params.toString());
}

function getNewLineText(text) {
	return text.replace(/(?:\r\n|\r|\n)/g, '<br>');
}

function buyPost(postId, price, actorId) {
	GuestAccess.onEnoughMoneyForShop(actorId, price, false, false, () => {
		Flux.Feed.buyPost(postId, () => {
			Flux.Feed.triggerUpdatePost(postId);
		});
	});
}

function getBuyRandomText(actorName, translation) {
	let hasText = true;
	let counter = 1;
	const textArray = [];
	while (hasText) {
		const text = Translations.get(translation + counter, actorName);
		counter++;

		if (text) {
			textArray.push(text);
		} else {
			hasText = false;
		}
	}

	return textArray[Math.floor(Math.random() * textArray.length)];
}

function getBuyText(actorName, translation) {
	return <div className="feed__need-buy__text">{getBuyRandomText(actorName, translation)}</div>;
}

const videoType = [Flux.Constants.Feed.Types.CLIP, Flux.Constants.Feed.Types.HOT_CLIP, Flux.Constants.Feed.Types.FP_HOT_CLIP];
const photoTypes = [Flux.Constants.Feed.Types.PHOTOS, Flux.Constants.Feed.Types.PHOTO_GALLERY, Flux.Constants.Feed.Types.FP_PHOTO_GALLERY];

function getVideoIcon(buyType) {
	switch (buyType) {
		case BUY_TYPES.PC:
			return <img src="/assets/img/feed/video_locked.svg" className="feed__play-locked" />;
		case BUY_TYPES.TG:
			return <img src="/assets/img/feed/video_locked.svg" className="feed__play-locked" />;
		default:
			return '';
	}
}

function getPhotoIcon(buyType) {
	switch (buyType) {
		case BUY_TYPES.PC:
			return <img className="feed__need-buy__icon" src="/assets/img/feed/content.svg" />;
		case BUY_TYPES.TG:
			return <img className="feed__need-buy__icon" src="/assets/img/feed/tippingGoal.svg" />;
		default:
			return '';
	}
}

function getBuyIcon(type, buyType) {
	if (videoType.includes(type)) {
		return getVideoIcon(buyType);
	} else if (photoTypes.includes(type)) {
		return getPhotoIcon(buyType);
	}
	return '';
}

export {
	getTimeFromSeconds,
	postType,
	postClipType,
	postPhotoType,
	isRecommended,
	postCommentType,
	tippingGoalType,
	userType,
	setUrlParameter,
	REPLY_SIGN,
	FREE_CONTENT,
	getButton,
	getNewLineText,
	getBuyButton,
	getBuyText,
	getBuyIcon,
	BUY_TYPES,
};
